import type {
  PantryNavApiItem,
  PantryNavApiResponse,
  PantryNavApiSubItem,
  PantryNavIconProps,
} from '@pantry/features'
import type { NavItemDef, NavSection } from '@pantry/ui'
import { LayoutDashboard, Settings } from 'lucide-react'
import { createElement } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import { matchPath } from 'react-router-dom'
import logger from '~/utils/logger'
import { resolveIcon } from './iconRegistry'

interface BuildSectionsArgs {
  api: PantryNavApiResponse | null | undefined
  storeId: string
  pathname: string
  navigate: NavigateFunction
}

const resolveStoreId = (
  path: string | null | undefined,
  storeId: string,
): string => (path ?? '').replace(':storeId', storeId)

const renderIcon = (
  iconName?: string | null,
  iconProps?: PantryNavIconProps | null,
) => {
  const Icon = resolveIcon(iconName)
  if (!Icon) {
    if (iconName) {
      logger.warn(`[PantryNav] unknown icon_name "${iconName}" — ignoring`)
    }
    return undefined
  }
  return createElement(Icon, {
    size: iconProps?.size ?? 16,
    strokeWidth: iconProps?.stroke_width ?? 1.7,
  })
}

const computeActive = (
  storeId: string,
  path?: string | null,
  end?: boolean,
  pathname?: string,
): boolean => {
  if (!path || !pathname) return false
  return !!matchPath(
    { path: resolveStoreId(path, storeId), end: !!end },
    pathname,
  )
}

const buildHandler = (
  item: PantryNavApiItem | PantryNavApiSubItem,
  storeId: string,
  navigate: NavigateFunction,
): (() => void) | undefined => {
  if (item.kind === 'route' && item.path) {
    const dest = resolveStoreId(item.path, storeId)
    return () => navigate(dest)
  }
  if (item.kind === 'external' && item.url) {
    const target = item.target ?? '_blank'
    return () => window.open(item.url as string, target)
  }
  return undefined
}

const mapSubItem = (
  sub: PantryNavApiSubItem,
  storeId: string,
  pathname: string,
  navigate: NavigateFunction,
): Omit<NavItemDef, 'subItems'> => ({
  id: sub.id,
  label: sub.label,
  icon: renderIcon(sub.icon_name, sub.icon_props),
  active:
    sub.kind === 'external'
      ? false
      : computeActive(storeId, sub.path, sub.match?.end, pathname),
  onClick: buildHandler(sub, storeId, navigate),
  ...(sub.badge != null ? { badge: sub.badge } : {}),
})

const mapItem = (
  item: PantryNavApiItem,
  storeId: string,
  pathname: string,
  navigate: NavigateFunction,
): NavItemDef => {
  const subItems = (item.sub_items ?? []).map((sub) =>
    mapSubItem(sub, storeId, pathname, navigate),
  )
  const hasSubItems = subItems.length > 0

  return {
    id: item.id,
    label: item.label,
    icon: renderIcon(item.icon_name, item.icon_props),
    active:
      item.kind === 'external'
        ? false
        : computeActive(storeId, item.path, item.match?.end, pathname),
    onClick:
      item.kind === 'group' ? undefined : buildHandler(item, storeId, navigate),
    ...(item.badge != null ? { badge: item.badge } : {}),
    ...(hasSubItems ? { subItems } : {}),
  }
}

const SAFE_FALLBACK_SECTIONS = (
  storeId: string,
  pathname: string,
  navigate: NavigateFunction,
): NavSection[] => {
  const base = `/s/${storeId}`
  return [
    {
      label: 'Overview',
      items: [
        {
          id: 'dashboard',
          label: 'Dashboard',
          icon: createElement(LayoutDashboard, { size: 16, strokeWidth: 1.7 }),
          active: !!matchPath(
            { path: `${base}/dashboard`, end: true },
            pathname,
          ),
          onClick: () => navigate(`${base}/dashboard`),
        },
      ],
    },
    {
      label: 'Store',
      items: [
        {
          id: 'settings',
          label: 'Settings',
          icon: createElement(Settings, { size: 16, strokeWidth: 1.7 }),
          active: !!matchPath(
            { path: `${base}/settings`, end: false },
            pathname,
          ),
          onClick: () => navigate(`${base}/settings`),
        },
      ],
    },
  ]
}

export const buildPantrySections = ({
  api,
  storeId,
  pathname,
  navigate,
}: BuildSectionsArgs): NavSection[] => {
  if (!api || !api.sections?.length) {
    return SAFE_FALLBACK_SECTIONS(storeId, pathname, navigate)
  }
  return api.sections.map((section) => ({
    label: section.label ?? undefined,
    items: section.items.map((item) =>
      mapItem(item, storeId, pathname, navigate),
    ),
  }))
}

export { SAFE_FALLBACK_SECTIONS }
